import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import type { DepartmentLocation, NurseInShift } from "../../../utils/models";

const WEEKDAY_ABBREVIATIONS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

/**
 * The 3-letter abbreviation of the weekday for a date (lowercase). Used as the key into
 * `location.dayAmounts` and `location.days` for day-specific configuration.
 */
export const weekdayAbbreviation = (date: DateTime): string => {
	return WEEKDAY_ABBREVIATIONS[date.weekdayISO];
};

/**
 * The master-schedule locations active for a given date — only locations that:
 * - Are non-empty
 * - Have the weekday in their `days` filter
 * - Either have a positive static `amount_required` for the weekday, **or** have a populated
 *   ratio ladder. A ladder can resolve to a positive census-driven amount later in the pipeline
 *   (`applyRatioLadderToLocations`); filtering on the static value alone would silently drop
 *   locations configured to be entirely census-driven (`amountRequired: 0` with a ladder).
 *
 * **No sort happens here.** Locations are returned in their original `department.locations` order;
 * all row ordering is the responsibility of Phase 2 (mapping invariant §5.8 — single centralized
 * sort). Phase 2's `compareValues` chain (custom-order → floor heuristic → natural string)
 * handles location ordering uniformly and honors the Global Manual Location Order automatically.
 */
export const getMasterScheduleLocations = (department: Department, date: DateTime): DepartmentLocation[] => {
	const weekday = weekdayAbbreviation(date);
	return department.locations.filter((location) => {
		const dayRequired = location.dayAmounts?.[weekday]?.amount_required ?? location.amountRequired;
		const hasLadder = (location.ratioLadder?.length ?? 0) > 0;
		return location.days.includes(weekday) && location.location !== "" && (dayRequired > 0 || hasLadder);
	});
};

/**
 * Apply the page-level location filter to a list of locations.
 *
 * If `selectedLocations` is non-empty, keep locations whose name appears in the selection
 * **or** whose name isn't part of the available list (preserving locations that aren't user-toggleable).
 * If empty, return the input unchanged.
 */
export const filterLocations = (locations: DepartmentLocation[], selectedLocations: string[], availableLocations: string[]): DepartmentLocation[] => {
	if (selectedLocations.length === 0) return locations;
	return locations.filter((location) => selectedLocations.includes(location.location) || !availableLocations.includes(location.location));
};

/**
 * Apply the page-level location filter to a list of nurse shifts.
 *
 * Nurses without a location pass through unchanged; nurses with a location must either be in
 * `selectedLocations` or have a location not present in `availableLocations` (same logic as
 * {@link filterLocations}).
 */
export const filterNurseShifts = (nurseShifts: NurseInShift[], selectedLocations: string[], availableLocations: string[]): NurseInShift[] => {
	if (selectedLocations.length === 0) return nurseShifts;
	return nurseShifts.filter((nurse) => !nurse.location || selectedLocations.includes(nurse.location) || !availableLocations.includes(nurse.location));
};
